import { z } from 'zod';
/**
 * An initiative tracker row. Used both for PCs and monsters/NPCs added during a fight.
 */
export const initiativeRowSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['pc', 'monster', 'custom']).default('custom'),
    init: z.number().nullable().default(null),
    hp: z.number().nullable().optional(),
    maxHp: z.number().nullable().optional(),
    ac: z.union([z.string(), z.number()]).nullable().optional(),
    conditions: z.array(z.string()).default([]),
    /** Reference back to the source monster/character id, when applicable. */
    ref: z.string().optional(),
})
    .catchall(z.unknown());
/**
 * All mutable, per-run state. This is what makes a session resumable: it is persisted
 * server-side as part of the instance and restored when the GM reopens the run.
 *
 * Note: the legacy `currentPhaseId` is intentionally absent — the phase system is removed.
 */
export const instanceStateSchema = z.object({
    visitedRooms: z.array(z.string()).default([]),
    /** Current HP per character, keyed by character name. */
    hp: z.record(z.number()).default({}),
    /** Per-character "active in party" flags; names listed here are benched. */
    inactiveCharacters: z.array(z.string()).default([]),
    notes: z.string().default(''),
    initiative: z.array(initiativeRowSchema).default([]),
    initiativeRound: z.number().default(1),
    initiativeActive: z.number().default(0),
    /** Free-form per-section progress (e.g. checked clues, revealed secrets). */
    sectionProgress: z.record(z.unknown()).default({}),
});
export const instanceStatusSchema = z.enum(['active', 'paused', 'completed']);
/**
 * Links a real person (see people.ts) to the PG they play in this run. The PG is referenced
 * by character name (the stable key used across the app today); `characterId` is stored for
 * forward-compatibility once characters gain ids.
 */
export const instancePlayerSchema = z.object({
    personId: z.string().optional(),
    characterName: z.string(),
    characterId: z.string().optional(),
});
/**
 * A single play session recorded when the GM "closes the table for the day". Captures what
 * happened so the next sitting can pick up where it left off.
 */
export const sessionLogEntrySchema = z.object({
    id: z.string(),
    /** ISO date/time the session took place (defaults to close time). */
    date: z.string(),
    endedAt: z.string().optional(),
    title: z.string().optional(),
    notes: z.string().default(''),
    /** Person ids (or, as a fallback, names) of who attended. */
    attendees: z.array(z.string()).default([]),
    durationMin: z.number().optional(),
});
/**
 * A run of an adventure. Created when the GM "starts" an adventure. Saved server-side so a
 * session that spans multiple real-world sittings can be paused and resumed. A single
 * adventure template can have many instances.
 */
export const instanceSchema = z.object({
    schemaVersion: z.literal(2).default(2),
    id: z.string(),
    adventureSlug: z.string(),
    /** GM-friendly run name, e.g. "Martedì col gruppo di Marco". */
    name: z.string(),
    status: instanceStatusSchema.default('active'),
    state: instanceStateSchema.default({
        visitedRooms: [],
        hp: {},
        inactiveCharacters: [],
        notes: '',
        initiative: [],
        initiativeRound: 1,
        initiativeActive: 0,
        sectionProgress: {},
    }),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** Snapshot of adventure title/cover so instance lists render without loading the template. */
    adventureTitle: z.string().optional(),
    adventureCover: z.string().optional(),
    /** The group (recurring party) this run belongs to, when started from a group. */
    groupId: z.string().optional(),
    /** People playing this run, each optionally linked to a PG. */
    players: z.array(instancePlayerSchema).default([]),
    /** History of play sessions captured via "close for the day". */
    sessionLog: z.array(sessionLogEntrySchema).default([]),
});
/** Lightweight summary for the "resume a run" list. */
export const instanceSummarySchema = z.object({
    id: z.string(),
    adventureSlug: z.string(),
    adventureTitle: z.string().optional(),
    adventureCover: z.string().optional(),
    name: z.string(),
    status: instanceStatusSchema,
    groupId: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Payload to create a new run from an adventure. */
export const createInstanceInputSchema = z.object({
    adventureSlug: z.string(),
    name: z.string().min(1).optional(),
    /** Start this run for a group (its members can be pre-linked as players). */
    groupId: z.string().optional(),
    players: z.array(instancePlayerSchema).optional(),
});
//# sourceMappingURL=instance.js.map